import React, { useState, useEffect } from 'react';
import { Box, Button, Card, CardContent, Typography, Chip, CircularProgress, Accordion, AccordionSummary, AccordionDetails, TextField } from '@mui/material';
import { EXCHANGE_API_CONFIGS } from './services/exchangeNetworkApis';
import { EXCHANGE_INDICES } from './types/networkStatus';

interface NetworkInfo {
  network: string;
  deposit_enabled: boolean;
  withdraw_enabled: boolean;
  withdraw_fee?: number;
  min_withdraw?: number;
  min_deposit?: number;
}

interface CurrencyInfo {
  symbol: string;
  name: string;
  networks: NetworkInfo[];
}

interface ExchangeResult {
  exchange: string;
  index: number;
  success: boolean;
  error?: string;
  data?: CurrencyInfo[];
  rawDataSample?: any;
  totalCurrencies?: number;
  depositEnabled?: number;
  withdrawEnabled?: number;
  currenciesWithFees?: number;
  timestamp?: number;
}

const ExchangeNetworkStatusTest: React.FC = () => {
  console.log('ExchangeNetworkStatusTest component rendering');
  const [results, setResults] = useState<ExchangeResult[]>([]);
  const [loading, setLoading] = useState<{ [key: number]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState('USDT');
  const [selectedCurrency, setSelectedCurrency] = useState<{ [key: number]: CurrencyInfo | null }>({});
  const [allData, setAllData] = useState<{ [key: number]: CurrencyInfo[] }>({});

  // Update selected currency when search term changes
  useEffect(() => {
    if (!searchTerm) {
      setSelectedCurrency({});
      return;
    }
    
    const newSelected: { [key: number]: CurrencyInfo | null } = {};
    
    Object.entries(allData).forEach(([indexStr, currencies]) => {
      const index = parseInt(indexStr);
      const found = currencies.find((c: CurrencyInfo) => 
        c.symbol.toUpperCase() === searchTerm.toUpperCase()
      );
      newSelected[index] = found || null;
    });
    
    setSelectedCurrency(newSelected);
  }, [searchTerm, allData]);

  // Working exchanges (with CORS support - can be called from browser)
  const WORKING_EXCHANGES = [
    { name: 'Upbit', index: EXCHANGE_INDICES.Upbit },
    { name: 'Bithumb', index: EXCHANGE_INDICES.Bithumb },
    { name: 'Binance', index: EXCHANGE_INDICES.Binance },
    { name: 'HTX', index: EXCHANGE_INDICES.HTX },
    { name: 'Bitget', index: EXCHANGE_INDICES.Bitget },
  ];
  
  // Exchanges that need backend proxy (no CORS or session protection)
  const BACKEND_ONLY_EXCHANGES = [
    { name: 'Coinone', index: EXCHANGE_INDICES.Coinone },
    { name: 'GateIO', index: EXCHANGE_INDICES.GateIO },
    { name: 'KuCoin', index: EXCHANGE_INDICES.KuCoin },
    { name: 'OKX', index: EXCHANGE_INDICES.OKX },
    { name: 'MEXC', index: EXCHANGE_INDICES.MEXC },
    { name: 'Bybit', index: EXCHANGE_INDICES.Bybit },
  ];
  
  // For testing all exchanges
  const ALL_EXCHANGES = [...WORKING_EXCHANGES, ...BACKEND_ONLY_EXCHANGES];

  const testExchange = async (name: string, index: number, autoSearch: boolean = true): Promise<ExchangeResult> => {
    const config = EXCHANGE_API_CONFIGS[index];
    if (!config) {
      return {
        exchange: name,
        index,
        success: false,
        error: 'No configuration found'
      };
    }

    try {
      setLoading(prev => ({ ...prev, [index]: true }));
      
      // Handle URL as function or string
      const url = typeof config.url === 'function' ? config.url() : config.url;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const rawData = await response.json();
      const parsed = await config.parseResponse(rawData);

      if (!Array.isArray(parsed)) {
        throw new Error('Parser did not return an array');
      }

      // Calculate statistics
      const depositEnabled = parsed.filter((c: CurrencyInfo) =>
        c.networks.some(n => n.deposit_enabled)
      ).length;

      const withdrawEnabled = parsed.filter((c: CurrencyInfo) =>
        c.networks.some(n => n.withdraw_enabled)
      ).length;

      const currenciesWithFees = parsed.filter((c: CurrencyInfo) =>
        c.networks.some(n => n.withdraw_fee !== undefined && n.withdraw_fee !== null)
      ).length;

      // Store all data for this exchange
      setAllData(prev => ({ ...prev, [index]: parsed }));
      
      // Find the searched currency
      const searchedCurrency = parsed.find((c: CurrencyInfo) => 
        c.symbol.toUpperCase() === searchTerm.toUpperCase()
      );
      
      if (searchedCurrency) {
        setSelectedCurrency(prev => ({ ...prev, [index]: searchedCurrency }));
      } else {
        setSelectedCurrency(prev => ({ ...prev, [index]: null }));
      }

      return {
        exchange: name,
        index,
        success: true,
        data: parsed,
        rawDataSample: rawData.data ? rawData.data[0] : rawData[0],
        totalCurrencies: parsed.length,
        depositEnabled,
        withdrawEnabled,
        currenciesWithFees,
        timestamp: Date.now()
      };
    } catch (error: any) {
      return {
        exchange: name,
        index,
        success: false,
        error: error.message || 'Unknown error',
        timestamp: Date.now()
      };
    } finally {
      setLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const testAllExchanges = async () => {
    setResults([]);
    setSelectedCurrency({});
    
    for (const exchange of ALL_EXCHANGES) {
      const result = await testExchange(exchange.name, exchange.index, true);
      setResults(prev => [...prev, result]);
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const searchCurrency = async () => {
    if (!searchTerm) {
      alert('Please enter a currency symbol to search');
      return;
    }
    
    // Clear previous results
    setSelectedCurrency({});
    
    // Test all exchanges and search for the currency
    for (const exchange of ALL_EXCHANGES) {
      // Only test exchanges that have results already or test all if no results
      if (results.length === 0 || results.find(r => r.index === exchange.index && r.success)) {
        const result = await testExchange(exchange.name, exchange.index, true);
        setResults(prev => {
          const filtered = prev.filter(r => r.index !== result.index);
          return [...filtered, result].sort((a, b) => a.index - b.index);
        });
        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  };

  const testSingleExchange = async (name: string, index: number) => {
    const result = await testExchange(name, index);
    setResults(prev => {
      const filtered = prev.filter(r => r.index !== index);
      return [...filtered, result].sort((a, b) => a.index - b.index);
    });
  };

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#0a0f1e', minHeight: '100vh' }}>
      <Typography variant="h3" sx={{ mb: 3, color: '#00ffff' }}>
        🌐 Exchange Network Status Test
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 2, color: '#fff' }}>
          Test all exchange network status APIs to verify deposit/withdrawal status and fees
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={testAllExchanges}
            sx={{ 
              bgcolor: '#2196f3',
              '&:hover': { bgcolor: '#1976d2' }
            }}
          >
            Test All Exchanges
          </Button>
          
          <TextField
            label="Search Currency"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                searchCurrency();
              }
            }}
            size="small"
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.1)',
              '& .MuiInputBase-input': { color: '#fff' },
              '& .MuiInputLabel-root': { color: '#aaa' }
            }}
          />
          
          <Button
            variant="outlined"
            onClick={searchCurrency}
            size="small"
            sx={{ 
              color: '#00ffff',
              borderColor: '#00ffff',
              '&:hover': { 
                borderColor: '#00cccc',
                bgcolor: 'rgba(0,255,255,0.1)' 
              }
            }}
          >
            Search
          </Button>
        </Box>
      </Box>

      {/* All Exchanges Grid */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, color: '#fff' }}>
          All Exchanges
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 2 }}>
          {ALL_EXCHANGES.map(exchange => {
            const result = results.find(r => r.index === exchange.index);
            const isLoading = loading[exchange.index];
            const currency = selectedCurrency[exchange.index];
            
            return (
              <Box key={exchange.index} sx={{ flex: '1 1 350px', minWidth: 0 }}>
                <Card sx={{ 
                  bgcolor: result?.success ? '#1a2f1a' : '#2a2a2a',
                  border: result?.success ? '2px solid #4caf50' : '1px solid #444'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: '#fff' }}>
                        {exchange.name}
                      </Typography>
                      <Chip 
                        label={`Index: ${exchange.index}`} 
                        size="small"
                        sx={{ bgcolor: '#333', color: '#fff' }}
                      />
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => testSingleExchange(exchange.name, exchange.index)}
                      disabled={isLoading}
                      sx={{ mb: 2, color: '#00ffff', borderColor: '#00ffff' }}
                    >
                      {isLoading ? <CircularProgress size={20} /> : 'Test'}
                    </Button>

                    {result && (
                      <>
                        {result.success ? (
                          <Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: '#aaa' }}>
                                  Total Currencies
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#fff' }}>
                                  {result.totalCurrencies}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: '#aaa' }}>
                                  With Fees
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#fff' }}>
                                  {result.currenciesWithFees}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: '#aaa' }}>
                                  Deposit Enabled
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#4caf50' }}>
                                  {result.depositEnabled}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: '#aaa' }}>
                                  Withdraw Enabled
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#4caf50' }}>
                                  {result.withdrawEnabled}
                                </Typography>
                              </Box>
                            </Box>

                            {currency && (
                              <Accordion sx={{ bgcolor: '#1a1a1a', mb: 2 }}>
                                <AccordionSummary>
                                  <Typography sx={{ color: '#00ffff' }}>
                                    {currency.symbol} ({currency.networks.length} networks) ▼
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                    {currency.networks.map((net, idx) => (
                                      <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: '#2a2a2a', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                                          {net.network}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#aaa' }}>
                                          Deposit: {net.deposit_enabled ? '✅' : '❌'} | 
                                          Withdraw: {net.withdraw_enabled ? '✅' : '❌'}
                                          {net.withdraw_fee !== undefined && ` | Fee: ${net.withdraw_fee}`}
                                          {net.min_withdraw !== undefined && ` | Min: ${net.min_withdraw}`}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            )}

                            <Typography variant="caption" sx={{ color: '#666' }}>
                              Tested at: {formatTimestamp(result.timestamp)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography color="error" variant="body2">
                            {result.error}
                          </Typography>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* API Endpoints Reference */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, color: '#fff' }}>
          📋 API Endpoints Reference
        </Typography>
        
        <Accordion sx={{ bgcolor: '#1a1a1a' }}>
          <AccordionSummary>
            <Typography sx={{ color: '#fff' }}>View API Endpoints ▼</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
              <Typography sx={{ color: '#4caf50', mb: 1 }}>
                <strong>Binance:</strong> https://www.binance.com/bapi/capital/v2/public/capital/getNetworkCoinAll
              </Typography>
              <Typography sx={{ color: '#4caf50', mb: 1 }}>
                <strong>Upbit:</strong> https://ccx.upbit.com/api/v1/status/network/wallet
                <br />
                <strong>Upbit Fees:</strong> https://ccx.upbit.com/api/v1/status/withdraw_fee
              </Typography>
              <Typography sx={{ color: '#4caf50', mb: 1 }}>
                <strong>Bitget:</strong> https://api.bitget.com/api/v2/spot/public/coins
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default ExchangeNetworkStatusTest;